import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, ApiError, type PointsEvolution } from '../api.js';
import AppHeader from '../components/AppHeader.js';
import PoolNav from '../components/PoolNav.js';
import styles from './ChartScreen.module.css';

/**
 * Ported from the legacy `.evolution-chart-container` in spirit, not in
 * pixels: the legacy version drew to a `<canvas>` with pan/zoom controls.
 * This is a plain inline SVG polyline per participant — no charting library,
 * no interactivity — because the point of this step (PLAN.md 5.7) is proving
 * the *data* is aggregated correctly (cumulative points per participant, one
 * step per scored match) with something visible to look at, not a polished
 * chart. Improving the rendering later doesn't require touching the data
 * shape this produces.
 */
/**
 * Referenced as custom properties rather than literals so the palette can
 * darken for the light theme (see `--chart-*` in styles/tokens.css) — the
 * dark theme's amber, cyan and lime are nearly invisible on white.
 *
 * Applied through `style` rather than the `stroke`/`fill` presentation
 * attributes. Both resolve `var()` in current browsers, but the CSS property
 * is the form actually specified to, so it is the one to rely on.
 */
const COLORS = Array.from({ length: 10 }, (_, i) => `var(--chart-${i + 1})`);

const MIN_WIDTH = 640;
const STEP_WIDTH = 28; // px per matchday, so a long tournament scrolls instead of squashing
const HEIGHT = 320;
const PADDING = { top: 16, right: 16, bottom: 40, left: 40 };

function EvolutionChart({ data }: { data: PointsEvolution }) {
  const { t } = useTranslation();
  const stepCount = data.matchdays.length;
  // The viewBox width must equal the rendered width attribute — otherwise the
  // browser's default preserveAspectRatio uniformly scales-and-centers the
  // (smaller) viewBox content inside the (larger) box, which for a viewBox
  // this much narrower than the box left the whole chart squeezed into an
  // invisible sliver near the middle instead of stretched to fill it.
  const renderWidth = Math.max(MIN_WIDTH, stepCount * STEP_WIDTH);
  const plotWidth = renderWidth - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const maxPoint = Math.max(1, ...data.series.flatMap((s) => s.points));

  const x = (index: number) => (stepCount <= 1 ? 0 : (index / (stepCount - 1)) * plotWidth);
  const y = (value: number) => plotHeight - (value / maxPoint) * plotHeight;

  return (
    <div className={styles.chartScroll}>
      <svg
        className={styles.svg}
        viewBox={`0 0 ${renderWidth} ${HEIGHT}`}
        width={renderWidth}
        height={HEIGHT}
        role="img"
        aria-label={t('chart.title')}
      >
        <g transform={`translate(${PADDING.left}, ${PADDING.top})`}>
          {/* y-axis gridlines at 0%, 50%, 100% of the max — just enough to read the shape. */}
          {[0, 0.5, 1].map((fraction) => (
            <g key={fraction}>
              <line
                x1={0}
                x2={plotWidth}
                y1={plotHeight - fraction * plotHeight}
                y2={plotHeight - fraction * plotHeight}
                className={styles.gridline}
              />
              <text
                x={-8}
                y={plotHeight - fraction * plotHeight}
                className={styles.axisLabel}
                textAnchor="end"
                dy="0.32em"
              >
                {Math.round(maxPoint * fraction)}
              </text>
            </g>
          ))}

          {data.series.map((s, i) => {
            const color = COLORS[i % COLORS.length]!;
            const points = s.points.map((value, index) => `${x(index)},${y(value)}`).join(' ');
            return (
              <g key={s.participantId}>
                <polyline points={points} fill="none" style={{ stroke: color }} strokeWidth={2} />
                {s.points.map((value, index) => (
                  <circle key={index} cx={x(index)} cy={y(value)} r={2.5} style={{ fill: color }} />
                ))}
              </g>
            );
          })}

          {data.matchdays.map((day, index) => (
            <text
              key={day.matchId}
              x={x(index)}
              y={plotHeight + 16}
              className={styles.axisLabel}
              textAnchor="middle"
            >
              {day.matchNumber}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
}

export default function ChartScreen() {
  const { t } = useTranslation();
  const { id = '', poolId = '' } = useParams();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['points-evolution', id, poolId],
    queryFn: () => api.getPointsEvolution(id, poolId),
  });

  if (isPending) {
    return (
      <main className="screen">
        <p className="muted">{t('common.loading')}</p>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="screen">
        <PoolNav groupId={id} poolId={poolId} />
        <p className="error">
          {error instanceof ApiError ? error.message : t('common.somethingWentWrong')}
        </p>
      </main>
    );
  }

  return (
    <main className="screen">
      <AppHeader groupId={id} poolId={poolId} />
      <PoolNav groupId={id} poolId={poolId} />

      {data.matchdays.length === 0 ? (
        <p className="muted">{t('chart.empty')}</p>
      ) : (
        <>
          <EvolutionChart data={data} />
          <ul className={styles.legend}>
            {data.series.map((s, i) => (
              <li key={s.participantId} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ background: COLORS[i % COLORS.length] }}
                />
                {s.displayName}
              </li>
            ))}
          </ul>
        </>
      )}
    </main>
  );
}
